/* Super Admin > Emails — every outbound email (newest first). Click a row to read
   the full content + delivery status. Mounted at /app/emails. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import useLoadState from "@/hooks/useLoadState";
import { toast } from "sonner";
import { Loader2, RefreshCw, MailCheck, MailWarning, Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  sent: { c: "bg-emerald-100 text-emerald-700", i: <MailCheck className="h-4 w-4 text-emerald-600" />, label: "Sent" },
  failed: { c: "bg-red-100 text-red-700", i: <MailWarning className="h-4 w-4 text-red-500" />, label: "Failed" },
  captured: { c: "bg-slate-200 text-slate-600", i: <Mail className="h-4 w-4 text-slate-400" />, label: "Not sent (no delivery)" },
};
const fmt = (v) => { try { return new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return "—"; } };

const EmailsAdminPage = ({ session }) => {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const headers = useMemo(() => ({
    "x-user-id": session?.user?.id || "admin",
    "x-user-role": session?.user?.role || "admin",
    "x-tenant-id": session?.tenant?.id || "tenant_1",
  }), [session]);

  const { state, data: rows, error, reload } = useLoadState(async () => {
    const r = await axios.get(`${API}/api/admin/notifications?limit=500`, { headers });
    return Array.isArray(r.data) ? r.data : [];
  }, { deps: [headers] });

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => `${r.subject} ${r.recipient_email} ${r.kind}`.toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <div className="max-w-5xl mx-auto" data-testid="emails-admin-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Emails</h1>
          <p className="text-sm text-slate-500 mt-1">Every outbound email, newest first. Click to read the full message and delivery status.</p>
        </div>
        <Button variant="outline" onClick={reload} data-testid="emails-refresh"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
      </div>

      <Input placeholder="Search subject, recipient, or type…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 h-10 max-w-md" data-testid="emails-search" />

      {state === "loading" ? (
        <div aria-busy="true" className="space-y-2" data-testid="emails-loading">
          <div className="text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading emails…</div>
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 rounded-lg bg-slate-100 animate-pulse" />)}
        </div>
      ) : state === "error" ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fef2f2] p-6 text-center" data-testid="emails-error">
          <div className="text-[15px] font-semibold text-[#991b1b]">Failed to load emails</div>
          <div className="text-[13px] text-[#4b5563] mt-1">{error?.message || "Something went wrong."}</div>
          <Button variant="outline" onClick={reload} className="mt-3" data-testid="emails-retry"><RefreshCw className="h-4 w-4 mr-2" />Retry</Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500 text-sm" data-testid="emails-empty">
          {q ? <>No emails match your filters. <button onClick={() => setQ("")} className="text-[#007a9e] font-semibold hover:underline" data-testid="emails-clear-filters">Clear filters</button></> : "No emails found."}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white divide-y">
          {filtered.map((r) => {
            const st = STATUS[r.status] || STATUS.captured;
            return (
              <button key={r.id} onClick={() => setSelected(r)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors" data-testid={`email-row-${r.id}`}>
                <span className="shrink-0">{st.i}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-slate-800 truncate">{r.subject || "(no subject)"}</div>
                  <div className="text-[12px] text-slate-500 truncate">To {r.recipient_email || "—"}{r.cc_emails?.length ? ` · cc ${r.cc_emails.join(", ")}` : ""} · {r.kind}</div>
                </div>
                <span className={`shrink-0 text-[11.5px] font-bold uppercase px-2 py-0.5 rounded ${st.c}`}>{st.label}</span>
                <span className="shrink-0 text-[12px] text-slate-400 w-40 text-right">{fmt(r.created_at)}</span>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="email-detail-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div className="min-w-0">
                <div className="text-lg font-bold text-slate-900">{selected.subject || "(no subject)"}</div>
                <div className="text-[12px] text-slate-500 mt-1">To <b>{selected.recipient_email || "—"}</b>{selected.cc_emails?.length ? <> · cc {selected.cc_emails.join(", ")}</> : null}</div>
                <div className="text-[12px] text-slate-400 mt-0.5">{selected.kind} · {fmt(selected.created_at)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700 shrink-0" data-testid="email-detail-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5">
              {(() => { const st = STATUS[selected.status] || STATUS.captured; return (
                <span className={`inline-block text-[11.5px] font-bold uppercase px-2 py-0.5 rounded mb-3 ${st.c}`}>{st.label}</span>); })()}
              {selected.delivery_error && <div className="text-[13px] text-red-600 mb-3">Delivery error: {selected.delivery_error}</div>}
              <div className="text-[14px] text-slate-700 whitespace-pre-wrap leading-relaxed">{selected.body || "(no content)"}</div>
              {selected.extra?.temp_password && (
                <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[13px]">
                  <span className="text-slate-500">Temporary password included:</span> <b className="font-mono">{selected.extra.temp_password}</b>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailsAdminPage;
